import i18n from "../plugins/i18n";
import { AxiosResponse } from "axios";
import { LocaleService } from "@/services/storage.service";
import {
  EXEC_PROD_STATUS_CANCELED,
  EXEC_PROD_STATUS_COMPLETED,
  EXEC_PROD_STATUS_DIRECTED,
  EXEC_PROD_STATUS_PROCESSING,
  EXEC_PROD_STATUS_REJECTED,
  EXEC_PROD_STATUS_RETURN,
  EXEC_PROD_STATUS_SUSPENDED,
  ID_INDIVIDUAL,
  ID_LEGAL_ENTITY,
} from "@/config/const";
import { Dictionary } from "vue-router/types/router";
import apiService from "@/services/api.service";
import { ref, unref, computed, customRef } from "vue";
import type { Ref, UnwrapRef, ComputedRef } from "vue";
import { format, differenceInMonths, formatISO } from "date-fns";
import { ru, kk } from "date-fns/locale";

export function translate<T>(data: T): T {
  try {
    const str = JSON.stringify(data);
    return JSON.parse(str.replace(new RegExp(`_${i18n.locale}`, "gi"), ""));
  } catch (e) {
    console.log(e);
    return data;
  }
}

export function getImageFromAssets(name: string) {
  return require(`@/assets/${name}`);
}

export function getDiffDays(
  date1: number | string,
  date2: number | string = new Date().getTime(),
) {
  const b = new Date(date2);
  const a = new Date(date1);
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
}
export const wait = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function isObjectEmpty(obj: object) {
  return Object.entries(obj).length === 0 && obj.constructor === Object;
}

export function getFileNameFromHeader(res: AxiosResponse) {
  const error = "error";
  const disposition: string = res.headers["content-disposition"];

  if (!disposition || disposition.indexOf("attachment") == -1) return error;

  const isCyrillic = disposition.toLowerCase().indexOf("utf-8") !== -1;
  const regex = isCyrillic
    ? /filename\*?=([^']*'')?([^;]*)/
    : /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const fileNameIndex = isCyrillic ? 2 : 1;

  const matches = regex.exec(disposition);
  if (!matches || !matches[fileNameIndex]) return error;

  const fileName = matches[fileNameIndex].replace(/['"]/g, "");

  return isCyrillic ? decodeURIComponent(fileName) : fileName;
}

export function blobToJson<T>(blob: Blob): Promise<T> {
  return new Promise((resolve, reject) => {
    const f = new FileReader();
    f.onload = () => {
      try {
        resolve(JSON.parse(f.result as string));
      } catch (e) {
        reject(new Error());
      }
    };
    f.onerror = () => reject(new Error());
    f.onabort = () => reject(new Error());

    f.readAsText(blob, "UTF-8");
  });
}

export function b64Encode(p: string) {
  return btoa(unescape(encodeURIComponent(p)));
}

export function b64DecodeUnicode(p: string) {
  // Going backwards: from bytestream, to percent-encoding, to original string.
  return decodeURIComponent(
    atob(p)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(""),
  );
}

export function toBinaryString(file: File): Promise<string> {
  return new Promise(function (resolve) {
    const reader = new FileReader();
    reader.onload = function (e) {
      resolve(e.target?.result as string);
    };
    reader.readAsBinaryString(file);
  });
}
export function fileToBase64(file: File): Promise<string> {
  return new Promise(function (resolve) {
    const reader = new FileReader();
    reader.onload = function (e) {
      const bytes = Array.from(new Uint8Array(e.target?.result as ArrayBuffer));
      const base64StringFile = btoa(
        bytes.map((item) => String.fromCharCode(item)).join(""),
      );
      resolve(base64StringFile);
    };
    reader.readAsArrayBuffer(file);
  });
}

export function isIINBIN(id: string): number | boolean {
  const isIndividual = () => ["0", "1", "2", "3"].includes(id.charAt(4));
  const getSuccessResult = () =>
    isIndividual() ? ID_INDIVIDUAL : ID_LEGAL_ENTITY;
  if (!id) return false;
  if (id.length != 12) return false;
  if (!/[0-9]{12}/.test(id)) return false;

  if (process.env.NODE_ENV !== "production") return getSuccessResult();

  //Проверяем контрольный разряд
  const b1 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const b2 = [3, 4, 5, 6, 7, 8, 9, 10, 11, 1, 2];
  const a = [];
  let controll = 0;
  for (let i = 0; i < 12; i++) {
    a[i] = parseInt(id.substring(i, i + 1));
    if (i < 11) controll += a[i] * b1[i];
  }
  controll = controll % 11;
  if (controll == 10) {
    controll = 0;
    for (let i = 0; i < 11; i++) controll += a[i] * b2[i];
    controll = controll % 11;
  }
  return controll == a[11] && getSuccessResult();
}

export function formatDate(date: string | Date, formatOption = "dd.MM.yyyy") {
  if (!date) return;
  if (date === "-") return date;

  let locale;
  if (i18n.locale == "ru") locale = ru;
  else if (i18n.locale == "kk") locale = kk;
  return format(date instanceof Date ? date : new Date(date), formatOption, {
    locale,
  });
}

export function downloadFileLink(res: AxiosResponse, fileName?: string) {
  const link = document.getElementById(
    "app-download-link",
  ) as HTMLAnchorElement;
  link.href = window.URL.createObjectURL(new Blob([res?.data || res]));
  link.setAttribute("download", fileName || getFileNameFromHeader(res));
  link.click();
}

export async function downloadFile(href: string) {
  const res = await apiService.get(
    `${href}${href.includes("?") ? "&" : "?"}lang=${LocaleService.getLocale()}`,
    { responseType: "blob" },
  );

  const link = document.getElementById(
    "app-download-link",
  ) as HTMLAnchorElement;
  link.href = window.URL.createObjectURL(new Blob([res.data]));
  link.setAttribute("download", getFileNameFromHeader(res));
  link.click();
}

export function cloneObject<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export function getExecProdStatusColor(status: number) {
  switch (status) {
    case EXEC_PROD_STATUS_SUSPENDED:
      return "#DDC809";
    case EXEC_PROD_STATUS_RETURN:
    case EXEC_PROD_STATUS_REJECTED:
    case EXEC_PROD_STATUS_CANCELED:
      return "#FE0000";
    case EXEC_PROD_STATUS_PROCESSING:
    case EXEC_PROD_STATUS_COMPLETED:
    case EXEC_PROD_STATUS_DIRECTED:
    default:
      return "#3A673C";
  }
}

export function debounce(func: () => void) {
  let timer: NodeJS.Timeout;
  return function (timeout: number) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func();
    }, timeout);
  };
}
export function sortObjectsByProperties(
  arr: Dictionary<string>[],
  sortKey: string,
) {
  console.log(arr);
  return arr.sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    console.log(aValue, bValue);
    if (aValue < bValue) return -1;
    if (aValue > bValue) return 1;
    return 0;
  });
}

export function addScript(src: string) {
  return new Promise(function (resolve, reject) {
    let shouldAppend = false;
    let el: HTMLScriptElement | null = document.querySelector(
      'script[src="' + src + '"]',
    );
    if (!el) {
      el = document.createElement("script");
      el.type = "text/javascript";
      el.async = true;
      el.src = src;
      shouldAppend = true;
    } else if (el.hasAttribute("data-loaded")) {
      resolve(el);
      return;
    }

    el.addEventListener("error", reject);
    el.addEventListener("abort", reject);
    el.addEventListener("load", function loadScriptHandler() {
      el?.setAttribute("data-loaded", "true");
      resolve(el);
    });

    if (shouldAppend) document.head.appendChild(el);
  });
}

export function hasScript(src: string) {
  const el: HTMLScriptElement | null = document.querySelector(
    'script[src="' + src + '"]',
  );
  return !!el;
}

export function cutTimeFromISODate(date: string | Date) {
  return formatISO(date, { representation: "date" });
}

export function getDiffMonths(d1: string, d2: string) {
  return differenceInMonths(d1, d2);
}

export function isDateValid(s: string) {
  const timestamp = Date.parse(s);
  return isNaN(timestamp) === false;
}

export function toISODate(s: string) {
  try {
    const res = s?.split(".").reverse().join("-");
    return isDateValid(res) ? res : "";
  } catch (e) {
    console.log(e);
    return "";
  }
}

export function checkIsNumber(term: string) {
  return /^\d+$/.test(term);
}

export function checkIsCyrillik(term: string) {
  return /[а-яА-ЯЁё]/.test(term);
}

export const debounceForRef = (fn: any, delay = 0, immediate = false) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: any) => {
    if (immediate && !timeout) fn(...args);
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

export const useDebouncedRef = <Type>(
  initialValue: Type,
  delay: number,
  immediate?: boolean,
) => {
  const state = ref(initialValue);
  const debouncedRef = customRef((track, trigger) => ({
    get() {
      track();
      return state.value;
    },
    set: debounceForRef(
      (value: any) => {
        state.value = value;
        trigger();
      },
      delay,
      immediate,
    ),
  }));
  return debouncedRef;
};

export const useTranslate = <Type>(
  data: Type,
  locale: string | undefined = undefined,
): [translation: ComputedRef<Type>, initial: Ref<UnwrapRef<Type>>] => {
  const initial = ref(unref(data));
  const translation = computed(() => {
    const str = JSON.stringify(initial.value);
    const selLocale = locale ?? i18n.locale;
    return JSON.parse(str.replace(new RegExp(`(_${selLocale})":`, "g"), `":`));
  });

  return [translation, initial];
};

// export async function getAndDownloadFile(href: string) {
//   let res;
//   try {
//     res = await apiService.get(
//       `${href}${
//         href.includes("?") ? "&" : "?"
//       }lang=${LocaleStorageService.getLocale()}`,
//       {
//         responseType: "blob",
//       }
//     );
//   } catch (e) {
//     return;
//   }

//   downloadFile(res);
// }

export function openHtmlInNewTab(html: string) {
  const w = window.open("about:blank");
  w!.document.write(html);
}
